// src/hooks/useOrgRole.js
//
// Tu rol en una organización.
//
// `GET /organizations` devuelve estadísticas pero **no la membresía**: el rol no
// viene en la lista (auth_service, `list_organizations`). El modal viejo lo
// tapaba con `organization.role || 'owner'`, que le daba interfaz de
// administrador a cualquier miembro. Quitar ese default sin poner nada en su
// lugar deja lo contrario: nadie ve nada.
//
// Así que se resuelve donde de verdad está, en la lista de miembros.
import { useEffect, useState } from 'react';
import OrganizationService from '../services/organization.service';
import { useAuth } from '../useAuth';

const MANAGE_ROLES = new Set(['owner', 'admin']);

// El sub-nav y la vista de la organización piden el mismo rol en el mismo
// render. Sin esto son dos peticiones idénticas por pantalla; compartiendo la
// promesa en vuelo, una.
const inFlight = new Map();

function fetchRole(organizationId, userId) {
  const key = `${organizationId}:${userId}`;
  if (!inFlight.has(key)) {
    inFlight.set(
      key,
      OrganizationService.listMembers(organizationId)
        .then((result) => result.data?.find((m) => m.user_id === userId)?.role?.toLowerCase() ?? null)
        // No se cachea el resultado, solo la petición: el rol puede cambiar
        // mientras la sesión sigue abierta y una caché lo dejaría congelado.
        .finally(() => inFlight.delete(key))
    );
  }
  return inFlight.get(key);
}

export default function useOrgRole(organizationId) {
  const { user } = useAuth();
  const userId = user?.id || user?.user_id || null;
  const [role, setRole] = useState(null);
  // `settled` y no `loading`: lo que necesita quien lo consume es saber si ya
  // hay respuesta, no si hay una petición en vuelo. Confundir las dos cosas es
  // lo que dejaba a `projectsLoading` mintiendo.
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    if (!organizationId || !userId) return undefined;
    let cancelled = false;
    setSettled(false);

    fetchRole(organizationId, userId).then((resolved) => {
      if (cancelled) return;
      setRole(resolved);
      setSettled(true);
    });

    return () => { cancelled = true; };
  }, [organizationId, userId]);

  return {
    role,
    settled,
    canManage: Boolean(role && MANAGE_ROLES.has(role)),
  };
}
